<script lang="ts">
  import { Plus, X, Pencil, Trash2, Check, Volume2, Search } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Button from '../ui/button.svelte';
  import Card from '../ui/card.svelte';
  import FlashcardDialog from './FlashcardDialog.svelte';
  import {
    flashcards,
    flashcardActions,
    filteredFlashcards,
    selectedTargetLanguage
  } from '../../stores/flashcards';

  let {
    open = false,
    onClose,
  }: {
    open: boolean;
    onClose: () => void;
  } = $props();

  let searchTerm = $state('');
  let selectedCategory = $state('');
  let deletingId = $state<string | null>(null);
  let showAddForm = $state(false);
  let editingCardId = $state<string | null>(null);

  const flashcardList = $derived($filteredFlashcards);
  
  const categories = $derived.by(() => {
    const cats = new Set<string>();
    for (const card of flashcardList) {
      if (card.category?.name) {
        cats.add(card.category.name);
      }
    }
    return Array.from(cats).sort();
  });

  const filteredCards = $derived.by(() => {
    return flashcardList.filter(card => {
      const matchesSearch =
        card.primary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.phonetic.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === '' || card.category?.name === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  });

  const languageName = $derived($selectedTargetLanguage?.name ?? '');

  function resetState() {
    searchTerm = '';
    selectedCategory = '';
    deletingId = null;
    showAddForm = false;
    editingCardId = null;
  }

  $effect(() => {
    if (!open) {
      resetState();
    }
  });

  function handleAddClick() {
    editingCardId = null;
    showAddForm = true;
  }

  function handleEditClick(cardId: string) {
    editingCardId = cardId;
    showAddForm = true;
  }

  function handleCloseForm() {
    showAddForm = false;
    editingCardId = null;
  }

  function handleToggleLearned(id: string) {
    flashcardActions.toggleLearned(id);
  }

  async function handleDelete(id: string) {
    if (deletingId) return;

    const confirmed = confirm($_('flashcards.manage.confirm_delete'));
    if (!confirmed) {
      return;
    }

    deletingId = id;
    try {
      await flashcardActions.deleteFlashcard(id);
    } catch (error) {
      console.error('Failed to delete flashcard:', error);
      alert(error instanceof Error ? error.message : $_('flashcards.manage.delete_error'));
    } finally {
      deletingId = null;
    }
  }

  function handlePlayAudio(card: any) {
    if (!card.audioUrl) return;
    const audio = new Audio(card.audioUrl);
    audio.play();
  }

    function handleClose() {
    onClose?.();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      handleClose();
    }
  }
</script>

{#if open}
  <div class="fixed inset-0 z-[96] flex flex-col bg-background"   
      role="dialog"
      aria-modal="true"
      tabindex="0"
      onkeydown={handleKeydown}
      >
    <div class="flex items-start justify-between px-6 pt-6 pb-4">
      <div class="space-y-2">
        <p class="text-xs uppercase tracking-[0.28em] text-muted-foreground">{$_('flashcards.manage.manage_title')}</p>
        <h2 class="font-semibold">{languageName || $_('flashcards.manage.manage_title')}</h2>
        {#if languageName}
          <p class="text-sm text-muted-foreground">{$_('flashcards.manage.selected_language')} {languageName}</p>
        {/if}
      </div>
      <button
        class="btn btn-close"
        onclick={onClose}
        aria-label={$_('aria.close_manage_flashcards')}
      >
        <X class="h-5 w-5" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-6 pb-24 space-y-4">
      <div class="space-y-4">
        <!-- Search Input -->
        <div class="relative">
          <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={$_('flashcards.manage.search_placeholder')}
            bind:value={searchTerm}
            class="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-muted text-foreground placeholder-muted-foreground shadow-soft-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <!-- Category Select -->
        <div>
          <label for="category-select" class="block text-sm font-medium text-foreground mb-2">
            {$_('flashcards.manage.category_label')}
          </label>
          <select
            id="category-select"
            bind:value={selectedCategory}
            class="form-select"
          >
            <option value="">{$_('flashcards.manage.all_categories')}</option>
            {#each categories as category}
              <option value={category}>{category}</option>
            {/each}
          </select>
        </div>

        <!-- Add Button -->
        <Button
          onclick={handleAddClick}
          class="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary/90 via-secondary/85 to-accent/85 text-primary-foreground shadow-soft-sm hover:shadow-lifted py-3"
        >
          <Plus class="h-4 w-4" />
          {$_('flashcards.manage.add_button')}
        </Button>
      </div>

      <!-- Flashcards Grid -->
      <div class="space-y-3 pb-4">
        {#if filteredCards.length === 0}
          <Card class="widget-card tone-header p-6 text-center">
            <p class="text-sm text-foreground">
              {searchTerm ? $_('flashcards.manage.no_results') : $_('flashcards.manage.no_cards')}
            </p>
          </Card>
        {:else}
          {#each filteredCards as card (card.id)}
            <Card class={`widget-card ${card.isLearned ? 'tone-list ring-2 ring-emerald-300/60' : 'tone-header'} p-4 transition-all`}>
              <div class="flex items-start justify-between gap-4">
                <div class="flex-1 min-w-0">
                  <p class="font-semibold text-foreground mb-1 truncate">{card.primary}</p>
                  <p class="text-base text-foreground/90 mb-1" dir="rtl">{card.target}</p>
                  {#if card.phonetic}
                    <p class="text-sm italic text-muted-foreground mb-2">{card.phonetic}</p>
                  {/if}
                  {#if card.category}
                    <span class="inline-flex items-center mt-2 px-2 py-1 rounded-full bg-muted text-xs font-semibold text-foreground">
                      {card.category.name}
                    </span>
                  {/if}
                </div>

                <!-- Action Buttons -->
                <div class="flex gap-2 flex-shrink-0">
                  {#if card.audioUrl}
                    <Button
                      onclick={() => handlePlayAudio(card)}
                      variant="ghost"
                      size="sm"
                      class="border border-border bg-muted p-2 text-primary hover:bg-muted/80"
                      title={$_('flashcards.manage.play_pronunciation')}
                    >
                      <Volume2 class="w-4 h-4" />
                    </Button>
                  {/if}

                  <Button
                    onclick={() => handleToggleLearned(card.id)}
                    variant={card.isLearned ? "outline" : "default"}
                    size="sm"
                    class={card.isLearned
                      ? "border-emerald-400 text-emerald-600 bg-muted hover:bg-muted/80"
                      : "bg-gradient-to-r from-emerald-600/80 to-emerald-500/80"}
                    title={card.isLearned ? $_('flashcards.manage.mark_unlearned') : $_('flashcards.manage.mark_learned')}
                  >
                    {#if card.isLearned}
                      <X class="w-4 h-4" />
                    {:else}
                      <Check class="w-4 h-4" />
                    {/if}
                  </Button>

                  <Button
                    onclick={() => handleEditClick(card.id)}
                    variant="ghost"
                    size="sm"
                    class="border border-border bg-muted p-2 text-primary hover:bg-muted/80"
                    title={$_('flashcards.manage.edit')}
                  >
                    <Pencil class="w-4 h-4" />
                  </Button>

                  <Button
                    onclick={() => handleDelete(card.id)}
                    variant="ghost"
                    size="sm"
                    class="border border-border bg-muted p-2 text-destructive hover:bg-muted/80"
                    disabled={deletingId === card.id}
                    title={$_('flashcards.manage.delete')}
                  >
                    <Trash2 class="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          {/each}
        {/if}
      </div>
    </div>
  </div>
{/if}

{#if editingCardId}
  <FlashcardDialog open={showAddForm} mode="edit" cardId={editingCardId} onClose={handleCloseForm} />
{:else}
  <FlashcardDialog open={showAddForm} mode="add" onClose={handleCloseForm} />
{/if}
